import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { meetingTypeFormSchema, type MeetingTypeFormValues } from './meetingTypeFormSchema'
import type { MeetingType, MeetingTypeUpsert } from '../types'

interface Props {
  visible: boolean
  initial: MeetingType | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: MeetingTypeUpsert) => void
  onHide: () => void
}

/**
 * Toplantı Türü ekle/düzenle formu (react-hook-form + zod). TR ad zorunlu; EN opsiyonel.
 *
 * <p>Yinelenen ad sunucudan `DUPLICATE_NAME` ile döner ve **alan hatası** olarak gösterilir (toast
 * değil): kullanıcı hangi alanı düzelteceğini görmeli.
 */
export default function MeetingTypeFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(meetingTypeFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible) reset({ nameTr: initial?.nameTr ?? '', nameEn: initial?.nameEn ?? '' })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: MeetingTypeFormValues) =>
    onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  return (
    <FormDialog
      visible={visible}
      header={initial ? t('meetingTypes.form.editTitle') : t('meetingTypes.form.newTitle')}
      submitLabel={t('meetingTypes.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="nameTr"
          label={t('meetingTypes.form.nameTr')}
          required
          autoFocus
        />
        <FormTextField control={control} name="nameEn" label={t('meetingTypes.form.nameEn')} />
      </div>
    </FormDialog>
  )
}
